import React, { useState, useEffect, useRef, useCallback } from "react";

import { useQuery } from "@tanstack/react-query";
import { PerformanceTrackingKeys } from "core-ui/client/react/core/constants/constants";
import { getTranslationsService } from "core-ui/client/react/core/services/translationService";
import StringUtil from "core-ui/client/src/app/StringUtil";
import PropTypes from "prop-types";

import ReferenceData from "../../constants/ReferenceData";

const TRANSLATIONS_QUERY_KEY = ReferenceData.QUERY_KEYS.TRANSLATIONS;

/**
 * Angular to react version
 * @param {*} param0
 * @returns
 */
const LoginForm = ({
    accu,
    authentication,
    authenticateInput,
    loggingIn,
    errorCode,
    errorMessageParams,
    showLoginUpd,
    registerButtonClickInput,
    openAccountClickInput,
    loginDomain
}) => {
    const { data: translations, isSuccess: translationsLoaded } = useQuery({
        queryKey: [TRANSLATIONS_QUERY_KEY],
        queryFn: getTranslationsService,
        staleTime: ReferenceData.DEFAULT_STALE_TIME
    });

    const inputUserNmEl = useRef(null);
    const inputPasswordEl = useRef(null);
    const [passwordType, setPasswordType] = useState("password");
    const [showEye, setShowEye] = useState(false);
    let clsShowEye = showEye ? "fa-eye" : "fa-eye-slash";
    const [showLogin, setShowLogin] = useState(showLoginUpd);
    if (showLoginUpd !== showLogin) {
        setShowLogin(showLoginUpd);
    }
    let clsShowLogin = !showLogin ? "hidden" : "";

    const [errorMessage, setErrorMessage] = useState("");
    const [submitInProgress, setSubmitInProgress] = useState(false);

    let disabledNotLoading = false;
    let disabledLoading = loggingIn || false;
    const [buttonLoadingDisabled, setButtonLoadingDisabled] = useState(false);
    let defaultText = "Sign In";
    let processingText = "Signing In";
    let allClasses = "pw-loader btn btn-primary text-center ";

    /**
     * authentication
     *    userId
     *    flowName
     *    errorMessage
     *    errorMessageParams
     *        code
     * @returns
     */

    const isDisabledLoading = () => {
        disabledLoading = disabledLoading !== undefined ? disabledLoading : buttonLoadingDisabled;
        return disabledLoading;
    };

    const isDisabled = () => {
        return disabledNotLoading || isDisabledLoading();
    };

    let clsDisabledLoading = isDisabledLoading() ? "disabled-loading" : "";

    const resetFormInput = () => {
        if (inputUserNmEl.current) {
            inputUserNmEl.current.value = "";
        }
        if (inputPasswordEl.current) {
            inputPasswordEl.current.value = "";
        }
    };

    const handlePopEvent = useCallback(() => {
        return () => window.removeEventListener("popstate", handlePopEvent);
    }, []);

    useEffect(() => {
        setShowLogin(showLoginUpd);
        window.addEventListener("popstate", handlePopEvent);
    }, [handlePopEvent, showLoginUpd]);

    useEffect(() => {
        let errorTag =
            errorCode > ""
                ? errorCode
                : authentication.errorMessage > ""
                  ? authentication.errorMessage
                  : "";
        let errorParams = errorMessageParams;

        if (
            errorTag &&
            errorTag > "" &&
            submitInProgress &&
            authentication.processing === "failed"
        ) {
            let errorText = translations?.logon[errorTag];

            if (String(errorTag).indexOf("locked") > -1) {
                errorText = StringUtil.supplant(errorText, {
                    EndLockTime: errorParams["EndLockTime"]
                });
            }

            if (String(errorTag).indexOf("passcode.incorrect") > -1) {
                inputPasswordEl.current.value = "";

                // redirects to login help page, clear only pw input
                // update error message in cases when user clicks on back button
                if (errorParams["attempts"] === "1") {
                    errorText = StringUtil.supplant(errorText, {
                        attempts: errorParams["attempts"]
                    });

                    setShowLogin(false);
                }
                // stays on login page
                if (errorParams["attempts"] === "2") {
                    errorText = StringUtil.supplant(errorText, {
                        attempts: errorParams["attempts"]
                    });
                }
            }
            errorText = String(errorText).replace("{", "").replace("}", "");
            setErrorMessage(errorText);

            setSubmitInProgress(false);
        } else if (authentication.processing === "loginsuccess") {
            setSubmitInProgress(false);
        }
    }, [
        errorCode,
        errorMessageParams,
        authentication.errorMessage,
        submitInProgress,
        authentication.processing,
        translations?.logon
    ]);

    const showHidePwd = () => {
        setShowEye(!showEye);
        let passwordType = showEye ? "password" : "text";
        setPasswordType(passwordType);
    };

    const updateUserId = (e) => {
        inputUserNmEl.current.value = e.target.value;
    };

    const updatePassword = (e) => {
        inputPasswordEl.current.value = e.target.value;
    };

    const redirectToLoginHelp = (e) => {
        e.preventDefault();
        resetFormInput();
        setSubmitInProgress(false);
        setErrorMessage("");
        setShowLogin(false);
        window.top.location.href = loginDomain + "/participant/#/loginHelp?accu=" + accu;
    };

    const submitFunction = (e) => {
        e.preventDefault();

        const formData = new FormData(e.target);
        const username = formData.get("usernameInput");
        const password = formData.get("passwordInput");

        setSubmitInProgress(true);
        setButtonLoadingDisabled(true);
        setErrorMessage("");
        authenticateInput({ username, password });
        localStorage.setItem(PerformanceTrackingKeys.PT_SIGNIN_CLICKED, new Date().getTime());
        localStorage.setItem(PerformanceTrackingKeys.PT_FIRST_LOAD, true);
        localStorage.setItem(PerformanceTrackingKeys.PT_SIDEBAR_FIRST_LOAD, true);
    };

    const registerButtonClick = (e) => {
        e.preventDefault();
        resetFormInput();
        setSubmitInProgress(false);
        setErrorMessage("");
        registerButtonClickInput();
        window.top.location.href = loginDomain + "/participant/#/register/?accu=" + accu;
    };

    const openAccountClick = (event) => {
        event.preventDefault();
        resetFormInput();
        setSubmitInProgress(false);
        setErrorMessage("");
        openAccountClickInput(event);
    };

    const handleOnKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            redirectToLoginHelp(e);
        }
    };

    return translationsLoaded ? (
        <div
            className={`col-xs-12 col-sm-3 col-sm-push-9 login-form ${clsShowLogin}`}
            data-testid="login-form"
        >
            <div className="prelogin-pod login-pod">
                <div className="form-group has-error" aria-live="assertive">
                    {errorMessage > "" ? (
                        <span id="helpBlock" className="help-block">
                            {errorMessage}
                        </span>
                    ) : null}
                </div>
                <h1 className="flush-top"> {translations?.logon?.logonTitle} </h1>
                <form id="authenticate" onSubmit={submitFunction}>
                    <div className="form-group">
                        <label htmlFor="usernameInput" className="sr-only">
                            {" "}
                            {translations?.logon?.username}{" "}
                        </label>
                        <div className="input-group">
                            <div className="input-group-addon">
                                <span
                                    className="em-user"
                                    role="img"
                                    aria-label={translations?.logon?.labels?.userLabel}
                                ></span>
                            </div>
                            <input
                                type="text"
                                className="form-control"
                                id="usernameInput"
                                name="usernameInput"
                                placeholder={translations?.logon?.username}
                                onChange={updateUserId}
                                ref={inputUserNmEl}
                                autoComplete="off"
                                aria-label={translations?.logon?.labels?.userLabel}
                            />{" "}
                            {/* data-ng-model="credentials.username" */}
                        </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="passwordInput" className="sr-only">
                            {translations?.logon?.password}
                        </label>
                        <div className="input-group">
                            <div className="input-group-addon">
                                <span
                                    className="em-lock"
                                    role="img"
                                    aria-label={translations?.logon?.labels?.passLabel}
                                ></span>
                            </div>
                            <input
                                type={passwordType}
                                className="form-control"
                                id="passwordInput"
                                name="passwordInput"
                                placeholder={translations?.logon?.password}
                                onChange={updatePassword}
                                ref={inputPasswordEl}
                                autoComplete="off"
                                minLength={8}
                                maxLength={64}
                                aria-label={translations?.logon?.labels?.passLabel}
                            />{" "}
                            {/* data-ng-model="credentials.password" */}
                            <div className="input-group-addon">
                                <span
                                    className={`fa-regular  ${clsShowEye}`}
                                    onClick={showHidePwd}
                                    onKeyUp={showHidePwd}
                                    role="button"
                                    tabIndex="0"
                                    aria-label={
                                        showEye
                                            ? translations?.logon?.labels?.showPassLabel
                                            : translations?.logon?.labels?.hidePassLabel
                                    }
                                ></span>
                                <div role="alert" className="sr-only">
                                    {showEye
                                        ? translations?.logon?.labels?.passwordHiddenAlert
                                        : translations?.logon?.labels?.passwordShownAlert}
                                </div>
                            </div>
                        </div>
                    </div>
                    <a
                        className="text-right center-block margin-bottom-default"
                        title="Need help logging in?"
                        onClick={redirectToLoginHelp}
                        tabIndex="0"
                        onKeyDown={handleOnKeyDown}
                    >
                        {translations?.logon?.loginHelp}
                    </a>{" "}
                    <div className="form-group clearfix">
                        <button
                            type="submit"
                            id="submit"
                            className={` btn-lg btn-block ${allClasses} ${clsDisabledLoading}`}
                            disabled={isDisabled()}
                            data-fired={processingText}
                        >
                            <span>{defaultText}</span>
                        </button>{" "}
                    </div>
                </form>
                <form id="portalRedirectForm" method="post">
                    <input type="hidden" id="synctoken" value="" />
                    <input type="hidden" id="accu" value="" />
                    <input type="hidden" id="gaId" value="" />
                </form>

                <button
                    className="btn btn-success btn-lg btn-block text-center"
                    onClick={registerButtonClick}
                >
                    {translations?.logon?.register}
                </button>

                {String(accu).toLowerCase() === "myerira" ? (
                    <a
                        onClick={openAccountClick}
                        className="text-right center-block margin-top-default margin-bottom-default"
                        title={translations?.logon?.openAccount + "?"}
                    >
                        {translations?.logon?.openAccount}
                    </a>
                ) : null}
            </div>
        </div>
    ) : null;
};

LoginForm.propTypes = {
    accu: PropTypes.string,
    authentication: PropTypes.object,
    authenticateInput: PropTypes.func,
    loggingIn: PropTypes.bool,
    errorCode: PropTypes.string,
    errorMessageParams: PropTypes.object,
    showLoginUpd: PropTypes.bool,
    registerButtonClickInput: PropTypes.func,
    openAccountClickInput: PropTypes.func,
    loginDomain: PropTypes.string
};

export default LoginForm;
